import routes from 'routes';
import type { Organisation } from 'store/organisation/models';
import {
  buildPwaEntityCaptureNavigateUrl,
  buildPwaEntityDetailsNavigateUrl,
  buildPwaEntityTaxReturnsNavigateUrl,
  buildPwaEntityTransactionsNavigateUrl,
  isPwaEntityTabKey,
  isPwaHiddenRoute,
  type PwaEntityTabKey,
} from 'utils/pwa-nav';

/** Persisted across PWA closes — restore last screen on next open. */
const LAST_WINDOW_KEY = 'gt-pwa-last-entity';
/** Same-tab listeners. `storage` does not fire in the tab that wrote. */
const LAST_ENTITY_EVENT = 'gt-pwa-last-entity-change';
/** Set when the user explicitly taps Home — survives a full reload of /main. */
const SKIP_HOME_RESTORE_KEY = 'gt-pwa-skip-home-restore';

export type PwaLastEntity = {
  organisationId?: string;
  tab?: PwaEntityTabKey;
  /** Full in-app path last visited on phone/PWA (entity, settings, etc.). */
  pathname?: string;
  /** Query string for that path, including the leading `?` when present. */
  search?: string;
};

const AUTH_OR_PUBLIC_PREFIXES = [
  routes.login,
  routes.signUp.root,
  routes.resetPassword.root,
  routes.termsOfUse,
  routes.privacyPolicy,
  routes.accessibilityStatement,
  routes.notFound,
  routes.activate,
] as const;

function isAuthOrPublicPath(pathname: string): boolean {
  return AUTH_OR_PUBLIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isPwaHomePickerPath(pathname: string): boolean {
  return (
    pathname === routes.main ||
    pathname === '/' ||
    pathname === routes.home ||
    pathname === routes.organisations.all
  );
}

/** Paths we may restore when the PWA opens on Home. */
export function isRestorablePwaPath(pathname: string): boolean {
  if (!pathname || isPwaHomePickerPath(pathname)) {
    return false;
  }
  if (isAuthOrPublicPath(pathname) || isPwaHiddenRoute(pathname)) {
    return false;
  }
  return true;
}

function withStoredSearch(pathname: string, search?: string): string {
  if (!search || pathname.includes('?')) return pathname;
  return `${pathname}${search.startsWith('?') ? search : `?${search}`}`;
}

function notifyPwaLastEntityChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LAST_ENTITY_EVENT));
}

/** Re-read last entity after a write in this tab, or a `storage` write in another. */
export function subscribePwaLastEntity(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === LAST_WINDOW_KEY) listener();
  };
  window.addEventListener(LAST_ENTITY_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(LAST_ENTITY_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function getPwaLastEntity(): PwaLastEntity | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(LAST_WINDOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PwaLastEntity>;
    const organisationId =
      parsed?.organisationId && typeof parsed.organisationId === 'string'
        ? parsed.organisationId
        : undefined;
    const pathname =
      parsed?.pathname && typeof parsed.pathname === 'string'
        ? parsed.pathname
        : undefined;
    const tab =
      parsed.tab && isPwaEntityTabKey(parsed.tab) ? parsed.tab : undefined;
    if (!organisationId && !pathname) {
      return null;
    }
    return {
      ...(organisationId ? { organisationId } : {}),
      ...(pathname ? { pathname } : {}),
      ...(tab ? { tab } : {}),
    };
  } catch {
    return null;
  }
}

export function setPwaLastEntity(
  organisationId: string,
  tab?: PwaEntityTabKey | string | null,
  pathname?: string | null
): void {
  if (typeof window === 'undefined' || !organisationId) {
    return;
  }
  try {
    const existing = getPwaLastEntity();
    const payload: PwaLastEntity = {
      organisationId,
      ...(tab && isPwaEntityTabKey(tab) ? { tab } : {}),
      ...(pathname && isRestorablePwaPath(pathname)
        ? { pathname }
        : existing?.pathname && isRestorablePwaPath(existing.pathname)
          ? { pathname: existing.pathname }
          : {}),
    };
    window.localStorage.setItem(LAST_WINDOW_KEY, JSON.stringify(payload));
    notifyPwaLastEntityChange();
  } catch {
    // private mode / quota
  }
}

/** Persist the last PWA screen. Home is stored too so reopen stays on Home. */
export function setPwaLastWindow(pathname: string, search = ''): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    // Closed on the entity picker — next launch must stay on Home (not jump into
    // an entity). Keep organisationId so footer Review/Capture/Returns can still
    // open the most recently viewed entity from Home.
    if (isPwaHomePickerPath(pathname)) {
      const existing = getPwaLastEntity();
      window.localStorage.setItem(
        LAST_WINDOW_KEY,
        JSON.stringify({
          pathname: routes.main,
          ...(existing?.organisationId
            ? { organisationId: existing.organisationId }
            : {}),
          ...(existing?.tab ? { tab: existing.tab } : {}),
        } satisfies PwaLastEntity)
      );
      notifyPwaLastEntityChange();
      return;
    }
    if (!isRestorablePwaPath(pathname)) {
      return;
    }
    const existing = getPwaLastEntity();
    const orgMatch = pathname.match(/\/entities\/organisation\/([^/?#]+)/);
    const organisationId = orgMatch?.[1] || existing?.organisationId;
    const payload: PwaLastEntity = {
      pathname,
      ...(organisationId ? { organisationId } : {}),
      ...(existing?.tab && orgMatch ? { tab: existing.tab } : {}),
    };
    const query = search.startsWith('?') || search === '' ? search : `?${search}`;
    if (query) payload.search = query;
    window.localStorage.setItem(LAST_WINDOW_KEY, JSON.stringify(payload));
    notifyPwaLastEntityChange();
  } catch {
    // private mode / quota
  }
}

export function clearPwaLastEntity(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(LAST_WINDOW_KEY);
    notifyPwaLastEntityChange();
  } catch {
    // ignore
  }
}

/** @deprecated Session gate removed — kept so older imports compile. */
export function hasPwaLastEntitySessionVisit(): boolean {
  return false;
}

/** Mark that Home was chosen explicitly — do not auto-restore last window. */
export function markSkipPwaHomeRestore(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(SKIP_HOME_RESTORE_KEY, '1');
  } catch {
    // ignore
  }
}

/** Consume the explicit-Home skip flag (one-shot). */
export function consumeSkipPwaHomeRestore(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (window.sessionStorage.getItem(SKIP_HOME_RESTORE_KEY) === '1') {
      window.sessionStorage.removeItem(SKIP_HOME_RESTORE_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/** @deprecated use markSkipPwaHomeRestore */
export function markPwaLastEntitySessionVisit(): void {
  markSkipPwaHomeRestore();
}

/** @deprecated no-op */
export function clearPwaLastEntitySessionVisit(): void {}

export function buildPwaLastEntityNavigateUrl(
  entity: Organisation,
  tab?: PwaEntityTabKey
): string {
  if (tab === 'capture') {
    return buildPwaEntityCaptureNavigateUrl(entity.uuid);
  }
  if (tab === 'transactions') {
    return buildPwaEntityTransactionsNavigateUrl(entity.uuid);
  }
  if (tab === 'vat') {
    return buildPwaEntityTaxReturnsNavigateUrl(entity.uuid);
  }
  return buildPwaEntityDetailsNavigateUrl(entity);
}

/**
 * Resolve where to send the user on PWA open from a stored last window.
 * Returns null when nothing valid can be restored (including “closed on Home”).
 */
export function resolvePwaLastWindowNavigateUrl(
  last: PwaLastEntity,
  entities: Organisation[] | undefined
): string | null {
  // Last screen was the entity picker — stay on Home.
  if (last.pathname && isPwaHomePickerPath(last.pathname)) {
    return null;
  }

  if (last.pathname && isRestorablePwaPath(last.pathname)) {
    const orgMatch = last.pathname.match(/\/entities\/organisation\/([^/?#]+)/);
    if (orgMatch) {
      const entity = entities?.find((o) => o.uuid === orgMatch[1]);
      if (!entity) {
        return null;
      }
      return withStoredSearch(last.pathname, last.search);
    }
    // Settings / other non-entity PWA screens
    return withStoredSearch(last.pathname, last.search);
  }

  // Legacy payloads with org id but no pathname → treat as closed on entity.
  if (!last.organisationId || !entities?.length) {
    return null;
  }
  const entity = entities.find((o) => o.uuid === last.organisationId);
  if (!entity) {
    return null;
  }
  return buildPwaLastEntityNavigateUrl(entity, last.tab);
}
